<template>
  <TModalCritical
    id="ask-owner-question"
    v-model:visible="visible"
    :title="modalTitle"
    :is-loading="isFormLoading"
    @close="close"
  >
    <template v-if="bookingId">
      <p class="mt-4">
        {{ t('message.success') }}
      </p>
    </template>

    <template v-else-if="isGuestBulkMessaging">
      <p class="mt-4">
        {{ t('guestBulkRequest.errorMessage') }}
      </p>
    </template>

    <template v-else>
      <TTipbox
        v-if="formError"
        variant="error"
      >
        <template v-if="formError?.Code === 'BOOKING_OVERLAPS_DATES'">
          {{ t('enquiryFailedForOverlappingDates') }}
        </template>
        <template v-else>
          {{ t('enquiryFailed') }}
        </template>
      </TTipbox>

      <TFormGroup
        label-for="ask-owner-question-date-range"
        :field="[r$.dateRange.start, r$.dateRange.end]"
      >
        <EstimateDateRange
          id="ask-owner-question-date-range"
          :disabled-dates="disabledDates"
          no-teleport
          @update="updateDates"
        />
      </TFormGroup>

      <EstimateErrorTipbox />

      <template v-if="!rvIsStatic">
        <TFormGroup
          :label="t('destination.label')"
          label-for="ask-owner-a-question-delivery"
          :field="r$.hasDestination"
          disable-touch-on-blur
        >
          <EstimatePlace
            input-id="ask-owner-a-question-delivery"
            :destination="bookingEstimateParams.destination?.name"
            :input-placeholder="t('destination.placeholder')"
            :countries="allowedCountries"
            @update-address="addressUpdated"
          />
        </TFormGroup>

        <EstimateCrossBorderTipbox
          v-if="crossBorderTravelDisallowed"
          :country="(rvCountry as COUNTRY_SHORTS)"
          class="mt-4"
        />
      </template>

      <TFormGroup
        v-if="!userPhoneNumber"
        :label="t('user.phoneNumber')"
        label-for="ask-owner-question-phone-number"
        :field="r$.phoneNumber"
      >
        <TInputPhone
          id="ask-owner-question-phone-number"
          v-model="phoneNumber"
          v-model:country-calling-code="countryPhoneCode"
        />
      </TFormGroup>

      <TFormGroup
        :field="r$.message"
        :min-length="MESSAGE_LENGTH_MIN"
      >
        <MoleculePersona
          :avatar="{ text: formatFullNameAsInitials(ownerName), src: rvOwnerAvatar }"
          :name="t('message.label', { name: ownerName })"
          size="xs"
          class="flex-row-reverse justify-between mb-2"
        />

        <TTextarea
          id="ask-owner-question-textarea"
          v-model="message"
          required
          :placeholder="t('message.placeholder')"
          :rows="isMobile ? 6 : 4"
        />
      </TFormGroup>
    </template>

    <template #button-primary="{ dismiss }">
      <EstimateButton
        v-if="bookingId"
        :enquiry-id="bookingId"
        inline-button
        page-source="InitialInquiry"
        @close-breakdown="dismiss()"
      />

      <TButton
        v-else-if="isGuestBulkMessaging"
        :to="localePath({ name: ' dashboard-profile', params: { dashboard: 'dashboard' } })"
       
      >
        {{ t('guestBulkRequest.cta') }}
      </TButton>

      <TButton
        v-else
       
        :disabled="isUserAlsoOwner || isFormLoading || r$.$error"
        class="ml-auto"
        @click="submitForm"
      >
        {{ t('sendMessage') }}
      </TButton>
    </template>

    <template #button-secondary>
      <TButton
        v-if="bookingId"
        variant="tertiary"
       
        @click="viewMessage()"
      >
        {{ t('viewMessage') }}
      </TButton>

      <TButton
        v-else-if="isGuestBulkMessaging"
       
        variant="tertiary"
        @click="close()"
      >
        {{ t('actions.cancel') }}
      </TButton>
    </template>
  </TModalCritical>
</template>

<script setup lang="ts">
import { minLength, required, requiredIf } from '@regle/rules'

import type { NuxtError } from '#app'
import type { BookingEstimateDestination, Nullable } from '~/types'
import type { BookingEnquiryParams } from '~/types/rental-api-aliases'
import type { BackendErrorInterface } from '~/types/backend-api-errors'
import type { COUNTRY_SHORTS } from '~/constants'
import type { SearchDates } from '~/types/search'

const emit = defineEmits<{
  close: []
}>()

const visible = defineModel<boolean>('visible', { default: false })

const { t } = useI18n()
const { isMobile } = useDevice()

const {
  rvAllowCrossBorderTravel,
  rvBookingSeasonStart,
  rvBookingSeasonEnd,
  rvCalendars,
  rvCountry,
  rvId,
  rvIsStatic,
  rvOwnerAvatar,
  rvOwnerFirstName,
  rvOwnerId,
  rvOwnerLastName,
  rvRVSellingDate,
  rvComputedTimezone,
} = useRvDetails()
const { bookingEstimateParams, isBookable, updateDestination } = useBookingEstimate()
const { userId, userPhoneNumber } = useUser()
const clickIdsCookie = useClickIdsCookie()

const disabledDates = computed(() => {
  return [
    ...getRvCalendarBlockedRangesFromSellingDate(rvRVSellingDate.value, rvComputedTimezone.value),
    ...getRvCalendarBlockedRangesFromBookingSeason(rvBookingSeasonStart.value, rvBookingSeasonEnd.value, rvComputedTimezone.value),
    ...getRvCalendarBlockedDates(rvCalendars.value, rvComputedTimezone.value),
  ]
})

const isGuestBulkMessaging = ref(false)
const bookingId = ref<Nullable<number>>(null)
const threadId = ref<Nullable<string>>(null)

const modalTitle = computed(() => {
  if (isGuestBulkMessaging.value) {
    return t('guestBulkRequest.title')
  }
  return bookingId.value ? t('message.sent') : t('title')
})

const ownerName = computed(() => formatNameAsUsername(rvOwnerFirstName.value, rvOwnerLastName.value))
const isUserAlsoOwner = computed(() => rvOwnerId.value === userId.value)

const route = useRoute()

async function updateDates(newDates: SearchDates) {
  await navigateTo({
    query: {
      ...route.query,
      StartDate: newDates?.start,
      EndDate: newDates?.end,
    },
  }, { replace: true })
}

function resetForm() {
  bookingId.value = null
  threadId.value = null
  isFormLoading.value = false
  formError.value = null
  isGuestBulkMessaging.value = false
  phoneNumber.value = ''
}

function close() {
  resetForm()
  emit('close')
}

const allowedCountries = computed(() => {
  if (rvAllowCrossBorderTravel.value || !rvCountry.value) {
    return []
  }
  return [rvCountry.value]
})

const crossBorderTravelDisallowed = computed(() => {
  if (rvAllowCrossBorderTravel.value) {
    return false
  }

  return bookingEstimateParams.value.destination?.country && bookingEstimateParams.value.destination.country !== rvCountry.value
})

async function addressUpdated(destination?: BookingEstimateDestination) {
  updateDestination(destination)
  r$.hasDestination.$touch()

  await navigateTo({ query: { ...route.query, PlaceId: destination?.placeId || undefined } }, { replace: true })
}

/**
 * Form data, computed data and validation
 */
const message = ref('')
const phoneNumber = ref('')
const countryPhoneCode = ref('')
const dateRange = computed(() => ({
  start: bookingEstimateParams.value.dateStart,
  end: bookingEstimateParams.value.dateEnd,
}))
const hasDestination = computed(
  () => Boolean(bookingEstimateParams.value.destination) || undefined,
)
const hasEstimateData = computed(() => isBookable.value || undefined)

const MESSAGE_LENGTH_MIN = 20

const { r$ } = useRegle(
  {
    dateRange,
    hasDestination,
    hasEstimateData,
    message,
    phoneNumber,
  },
  {
    dateRange: {
      start: { required },
      end: { required },
    },
    hasDestination: {
      required: requiredIf(() => !rvIsStatic.value),
    },
    hasEstimateData: {
      required,
    },
    message: {
      required,
      minLength: minLength(MESSAGE_LENGTH_MIN),
    },
    phoneNumber: {
      required: requiredIf(() => !userPhoneNumber.value),
      phoneValid: phoneValid(() => countryPhoneCode.value),
    },
  },
)

/**
 * Form submission
 */
const { $captureError, $rentals } = useNuxtApp()
const { getSession } = useAuth()
const isFormLoading = ref(false)
const formError = ref<Nullable<BackendErrorInterface>>(null)

async function submitForm() {
  if (isFormLoading.value || isUserAlsoOwner.value) {
    return
  }

  const { valid } = await r$.$validate()

  if (!valid) {
    return
  }

  let newBookingId: Nullable<number> = null

  isFormLoading.value = true

  const enquiryParams: BookingEnquiryParams = {
    RVId: rvId.value,
    OwnerId: rvOwnerId.value,
    StartDate: bookingEstimateParams.value.dateStart,
    ShortIntroduce: message.value,
    Country: rvCountry.value, // Should this be destination?  bookingEstimateParams.value.destination?.country,
    EndDate: bookingEstimateParams.value.dateEnd,
    Adults: bookingEstimateParams.value.adults,
    Children: bookingEstimateParams.value.children,
    Destination: bookingEstimateParams.value.destination?.name,
    DestinationLatitude: bookingEstimateParams.value.destination?.latitude,
    DestinationLongitude: bookingEstimateParams.value.destination?.longitude,
    DestinationPlaceId: bookingEstimateParams.value.destination?.placeId,
    Pets: bookingEstimateParams.value.pets ? 1 : 0,
    ClickIds: parseLiveClickIds(clickIdsCookie.value),
  }

  try {
    // Set user's phone number if provided.
    if (phoneNumber.value && !userPhoneNumber.value) {
      await $rentals('/api/user-profiles/update-phone-number', {
        method: 'PUT',
        body: {
          PhoneNumber: phoneNumber.value ?? '',
          CountryPhoneCode: countryPhoneCode.value ?? '',
        },
      })

      await getSession()
    }

    newBookingId = await $rentals('/api/bookings/enquiry', {
      method: 'POST',
      body: enquiryParams,
    })

    if (newBookingId) {
      await sendMessage(newBookingId)
    }
  }
  catch (error) {
    const err = error as NuxtError
    const errData = err.data as BackendErrorInterface
    if (errData.Code === 'WAIT_FOR_VERIFICATION') {
      isGuestBulkMessaging.value = true
      return
    }

    formError.value = errData
    $captureError(err)
  }
  finally {
    bookingId.value = newBookingId
    isFormLoading.value = false
  }
}

async function sendMessage(BookingId: number) {
  if (!BookingId) {
    return
  }

  const newThreadId = await $rentals('/api/Message/get-id-thread', {
    params: {
      receiver: rvOwnerId.value,
      RVId: rvId.value,
      BookingId,
    },
  })

  threadId.value = newThreadId

  await $rentals('/api/Message/send', {
    method: 'POST',
    body: {
      MessageContent: message.value,
      ThreadId: threadId.value,
      IsEnquiryBooking: true,
    },
  })

  message.value = ''
  r$.$reset()
}

const localePath = useLocalePath()
const { isWebView } = usePlatform()

async function viewMessage() {
  if (!bookingId.value) {
    return
  }

  visible.value = false

  if (isWebView.value && threadId.value) {
    messageAppToOpenInbox(threadId.value)
    return
  }

  await navigateTo({
    path: localePath({
      name: 'dashboard-bookings-id-index',
      params: { id: bookingId.value.toString(), dashboard: 'dashboard' },
    }),
  })
}
</script>

<i18n src="~~/i18n/locales/common/guest-bulk-request.json" lang="json" />

<i18n lang="json">
{
  "en": {
    "title": "Ask a question",
    "sendMessage": "Send message",
    "viewMessage": "View message",
    "message": {
      "label": "Message {name}",
      "placeholder": "Introduce yourself, share your travel plans, and ask any questions you have.",
      "success": "Don’t forget—you can still request to book this RV while awaiting their response. Get first dibs on your preferred dates by requesting to book now.",
      "sent": "Message sent!"
    },
    "enquiryFailed": "Enquiry couldn’t be sent",
    "enquiryFailedForOverlappingDates": "This range of dates is not available anymore",
    "destination": {
      "label": "Where are you travelling to?",
      "placeholder": "Search for an address, campsite, city, town, or park"
    }
  },
  "fr": {
    "title": "Poser une question",
    "sendMessage": "Envoyer le message",
    "viewMessage": "Voir message",
    "message": {
      "label": "Message {name}",
      "placeholder": "Présentez-vous, partagez vos plans de voyage et posez vos questions.",
      "success": "N’oubliez pas - vous pouvez quand même demander de réserver ce VR en attendant leur réponse. Soyez le premier en ligne pour vos dates préférées en demandant de le réserver dès maintenant.",
      "sent": "Message envoyé!"
    },
    "enquiryFailed": "La demande n’a pas été envoyée.",
    "enquiryFailedForOverlappingDates": "Cette plage de dates n'est plus disponible",
    "destination": {
      "label": "Où voyagez-vous?",
      "placeholder": "Rechercher une adresse, un site de camping, une ville ou un parc"
    }
  }
}
</i18n>
